import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { FileUp, LayoutTemplate, Plus, Presentation as PresentationIcon, Cloud as GoogleIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { sprekersApi } from '../api';
import { getGoogleConfig } from '../google/googleConfig';
import { describeGoogleError, pickPresentation, requestDriveFileToken } from '../google/googleSlides';
import { loadGoogleIdentityServices, loadGooglePicker } from '../google/googleScripts';
import useGoogleSlidesAccess from '../google/useGoogleSlidesAccess';
import { useSlidesTemplates } from '../hooks/useTemplates';
import { PRESENTATION_TYPES } from '../labels';
import type { PresentationDetail, PresentationType } from '../types';

type Source = 'studio' | 'pdf' | 'google' | 'template';

// Alleen als hint voor de gebruiker; de backend (MAX_SPEAKER_PDF_IMPORT_*) is
// de bron van waarheid en valideert bestandsinhoud, grootte en paginaaantal.
const PDF_MAX_MB = 50;
const PDF_MAX_PAGES = 100;

interface Props {
  engagementId: string;
  onCreated: (presentation: PresentationDetail) => void;
}

/**
 * Keuze bij het aanmaken van een presentatie: Zoea Studio (bestaande
 * editor), een PDF importeren (elke pagina wordt een afbeeldingsslide), een
 * Google Slides-presentatie importeren (via een PDF-export) of een nieuwe
 * presentatie maken vanuit een opgeslagen sjabloon van de organisatie. De Google
 * Slides-optie bestaat alleen voor gebruikers die de server voor de pilot heeft
 * toegelaten; de sjabloonoptie alleen als er een slides-sjabloon bestaat.
 */
export default function PresentationSourceChooser({ engagementId, onCreated }: Props) {
  const [source, setSource] = useState<Source>('studio');
  const [titleDraft, setTitleDraft] = useState('');
  const [presentationType, setPresentationType] = useState<PresentationType>('slides');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const googleConfig = getGoogleConfig();
  // Alleen voor server-toegelaten pilotgebruikers; voor iedereen anders bestaat de optie niet.
  const googleAllowed = useGoogleSlidesAccess();
  const slidesTemplates = useSlidesTemplates();
  const templateAllowed = slidesTemplates.length > 0;
  const [templateId, setTemplateId] = useState('');
  // Bewerkbaar = echte tekst/afbeeldingen/vormen (Slides API); visueel = gerenderde pagina's (PDF-export).
  const [googleMode, setGoogleMode] = useState<'editable' | 'visual'>('editable');

  useEffect(() => {
    // Alvast laden: de Google-popup moet direct vanuit de klik kunnen openen,
    // anders blokkeert de browser hem. Fouten worden pas bij de klik getoond.
    if (source === 'google' && googleConfig && googleAllowed) {
      loadGoogleIdentityServices().catch(() => undefined);
      loadGooglePicker().catch(() => undefined);
    }
  }, [source]); // eslint-disable-line react-hooks/exhaustive-deps

  const chooseSource = (next: Source) => {
    setSource(next);
    setError(null);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const chosen = event.target.files?.[0] || null;
    setError(null);
    if (chosen && !chosen.name.toLowerCase().endsWith('.pdf')) {
      setFile(null);
      setError('Kies een PDF-bestand (.pdf).');
      return;
    }
    if (chosen && chosen.size > PDF_MAX_MB * 1024 * 1024) {
      setFile(null);
      setError(`Dit bestand is te groot (maximaal ${PDF_MAX_MB} MB).`);
      return;
    }
    setFile(chosen);
  };

  const createStudioPresentation = async () => {
    setBusy(true);
    setError(null);
    try {
      onCreated(await sprekersApi.createPresentation(
        engagementId, { title: titleDraft || null, presentation_type: presentationType },
      ));
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Aanmaken mislukt');
    } finally {
      setBusy(false);
    }
  };

  const importPdf = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      onCreated(await sprekersApi.importPdfPresentation(engagementId, file, titleDraft || undefined));
    } catch (err: any) {
      const message = typeof err.response?.data?.detail === 'string'
        ? err.response.data.detail
        : 'Importeren mislukt. Controleer het bestand en probeer opnieuw.';
      setError(message);
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  const importFromGoogle = async () => {
    if (!googleConfig || !googleAllowed) return;
    setBusy(true);
    setError(null);
    try {
      // Het access-token bestaat alleen in deze functie (nooit in state of storage).
      const accessToken = await requestDriveFileToken(googleConfig.clientId);
      const picked = await pickPresentation(accessToken, googleConfig);
      if (!picked) return; // geannuleerd
      const payload = { file_id: picked.id, access_token: accessToken, title: titleDraft || undefined };
      onCreated(googleMode === 'editable'
        ? await sprekersApi.importGoogleSlidesEditable(engagementId, payload)
        : await sprekersApi.importGoogleSlidesPresentation(engagementId, payload));
    } catch (err: any) {
      const message = describeGoogleError(err, 'Importeren uit Google Slides mislukt. Probeer het opnieuw.');
      setError(message);
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  const createFromTemplate = async () => {
    if (!templateId) return;
    setBusy(true);
    setError(null);
    try {
      onCreated(await sprekersApi.createPresentationFromTemplate(
        engagementId, { template_id: templateId, title: titleDraft || undefined },
      ));
    } catch (err: any) {
      const message = typeof err.response?.data?.detail === 'string'
        ? err.response.data.detail
        : 'Presentatie maken uit sjabloon mislukt. Probeer het opnieuw.';
      setError(message);
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  const optionCount = 2 + (googleAllowed ? 1 : 0) + (templateAllowed ? 1 : 0);

  const optionClass = (active: boolean) => `flex flex-col items-start gap-1 rounded-lg border p-3 text-left text-sm transition-colors ${
    active ? 'border-teal-500 bg-teal-50' : 'border-slate-200 hover:bg-slate-50'
  }`;

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">Nog geen presentatie voor deze spreekbeurt. Hoe wil je beginnen?</p>

      <div role="radiogroup" aria-label="Soort presentatie" className={`grid gap-2 ${optionCount === 4 ? 'sm:grid-cols-2 lg:grid-cols-4' : optionCount === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
        <button
          type="button" role="radio" aria-checked={source === 'studio'} disabled={busy}
          className={optionClass(source === 'studio')} onClick={() => chooseSource('studio')}
        >
          <span className="flex items-center gap-2 font-medium text-slate-900">
            <PresentationIcon className="h-4 w-4" /> Zoea Studio
          </span>
          <span className="text-xs text-slate-500">Maak een presentatie met de Zoea-editor.</span>
        </button>
        <button
          type="button" role="radio" aria-checked={source === 'pdf'} disabled={busy}
          className={optionClass(source === 'pdf')} onClick={() => chooseSource('pdf')}
        >
          <span className="flex items-center gap-2 font-medium text-slate-900">
            <FileUp className="h-4 w-4" /> PDF importeren
          </span>
          <span className="text-xs text-slate-500">Gebruik een bestaande presentatie als PDF.</span>
        </button>
        {templateAllowed && (
          <button
            type="button" role="radio" aria-checked={source === 'template'} disabled={busy}
            className={optionClass(source === 'template')} onClick={() => chooseSource('template')}
          >
            <span className="flex items-center gap-2 font-medium text-slate-900">
              <LayoutTemplate className="h-4 w-4" /> Vanuit sjabloon
            </span>
            <span className="text-xs text-slate-500">Begin met een opgeslagen sjabloon van je organisatie.</span>
          </button>
        )}
        {googleAllowed && (
          <button
            type="button" role="radio" aria-checked={source === 'google'} disabled={busy}
            className={optionClass(source === 'google')} onClick={() => chooseSource('google')}
          >
            <span className="flex items-center gap-2 font-medium text-slate-900">
              <GoogleIcon className="h-4 w-4" /> Google Slides
            </span>
            <span className="text-xs text-slate-500">Importeer een presentatie uit Google Slides.</span>
          </button>
        )}
      </div>

      {source === 'template' && templateAllowed && (
        <div className="space-y-3">
          <p className="text-sm text-slate-500">
            De presentatie krijgt een eigen kopie van de slides uit het sjabloon. Het sjabloon blijft ongewijzigd en jouw
            presentatie blijft werken als het sjabloon later wordt aangepast of verwijderd. Geïmporteerde slides zijn
            visueel: de opmaak blijft behouden, maar tekstvakken zijn niet los te bewerken.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={templateId} onValueChange={setTemplateId} disabled={busy}>
              <SelectTrigger className="w-64" aria-label="Sjabloon"><SelectValue placeholder="Kies een sjabloon" /></SelectTrigger>
              <SelectContent>
                {slidesTemplates.map((template) => (
                  <SelectItem key={template.id} value={template.id}>
                    {template.name} ({template.slide_count ?? 0} slides)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              value={titleDraft}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitleDraft(e.target.value)}
              placeholder="Titel (optioneel, standaard de sjabloonnaam)"
              aria-label="Titel"
              disabled={busy}
              className="max-w-xs"
            />
            <Button onClick={createFromTemplate} disabled={!templateId || busy}>
              <LayoutTemplate className="mr-2 h-4 w-4" />
              {busy ? 'Bezig met aanmaken...' : 'Presentatie maken'}
            </Button>
          </div>
          {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
        </div>
      )}

      {source === 'studio' && (
        <div className="flex flex-wrap gap-2">
          <Input
            value={titleDraft}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitleDraft(e.target.value)}
            placeholder="Titel (optioneel)"
            aria-label="Titel"
            className="max-w-xs"
          />
          <Select value={presentationType} onValueChange={(value: PresentationType) => setPresentationType(value)}>
            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PRESENTATION_TYPES.map((option) => (
                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={createStudioPresentation} disabled={busy}>
            <Plus className="mr-2 h-4 w-4" />
            Presentatie aanmaken
          </Button>
        </div>
      )}

      {source === 'pdf' && (
        <div className="space-y-3">
          <p className="text-sm text-slate-500">
            Elke pagina van de PDF wordt een slide (maximaal {PDF_MAX_PAGES} pagina&apos;s en {PDF_MAX_MB} MB). Animaties
            en video uit de oorspronkelijke presentatie worden niet meegenomen. Je kunt de slides daarna gewoon
            ordenen en live bedienen.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              ref={fileInputRef} type="file" accept="application/pdf,.pdf" onChange={handleFileChange}
              aria-label="PDF-bestand" disabled={busy} className="max-w-xs"
            />
            <Input
              value={titleDraft}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitleDraft(e.target.value)}
              placeholder="Titel (optioneel, standaard de bestandsnaam)"
              aria-label="Titel"
              disabled={busy}
              className="max-w-xs"
            />
            <Button onClick={importPdf} disabled={!file || busy}>
              <FileUp className="mr-2 h-4 w-4" />
              {busy ? 'Bezig met importeren...' : 'PDF importeren'}
            </Button>
          </div>
          {busy && <p className="text-sm text-slate-500">De pagina&apos;s worden omgezet; dit kan even duren.</p>}
          {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
        </div>
      )}

      {source === 'google' && googleConfig && googleAllowed && (
        <div className="space-y-3">
          <p className="text-sm text-slate-500">
            Kies een presentatie in Google Slides. Zoea krijgt alleen toegang tot de presentatie die je kiest, leest hem
            alleen en schrijft nooit terug naar Google. Animaties, video en spreeknotities worden niet meegenomen.
          </p>
          <div role="radiogroup" aria-label="Importeren als" className="space-y-2">
            <label className="flex cursor-pointer items-start gap-2 text-sm">
              <input
                type="radio" name="google-mode" checked={googleMode === 'editable'} disabled={busy}
                onChange={() => setGoogleMode('editable')} className="mt-1"
              />
              <span>
                <span className="font-medium text-slate-900">Bewerkbare presentatie</span>
                <span className="block text-xs text-slate-500">
                  Tekst, afbeeldingen, vormen en achtergronden worden losse onderdelen die je in Zoea Studio kunt
                  aanpassen. Wat niet kan worden overgenomen (bijvoorbeeld tabellen) zie je na het importeren.
                </span>
              </span>
            </label>
            <label className="flex cursor-pointer items-start gap-2 text-sm">
              <input
                type="radio" name="google-mode" checked={googleMode === 'visual'} disabled={busy}
                onChange={() => setGoogleMode('visual')} className="mt-1"
              />
              <span>
                <span className="font-medium text-slate-900">Als afbeeldingen (visueel)</span>
                <span className="block text-xs text-slate-500">
                  Elke pagina wordt een afbeelding: precies zoals in Google, maar niet te bewerken.
                </span>
              </span>
            </label>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              value={titleDraft}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitleDraft(e.target.value)}
              placeholder="Titel (optioneel, standaard de naam in Google)"
              aria-label="Titel"
              disabled={busy}
              className="max-w-xs"
            />
            <Button onClick={importFromGoogle} disabled={busy}>
              <GoogleIcon className="mr-2 h-4 w-4" />
              {busy ? 'Bezig met importeren...' : 'Kies presentatie in Google'}
            </Button>
          </div>
          {busy && <p className="text-sm text-slate-500">Even geduld; rond de keuze in Google af en wacht op de import.</p>}
          {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
        </div>
      )}
    </div>
  );
}
